import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref, type RefObject } from 'react'
import { Check } from 'lucide-react'
import { useFullscreenHost } from '@/lib/fullscreen'
import { cx } from '@/lib/cx'
import styles from './ContextMenu.module.css'
import { isMobileNow } from '@/lib/mobile'

/**
 * Меню по правой кнопке (канал, категория, группа, сообщение): встаёт у курсора и не вылезает
 * за край окна; закрывается кликом мимо, Esc, прокруткой и сменой размера окна — как меню
 * пользователя и сервера. Ширина — по самому длинному пункту: русские подписи длиннее английских.
 * align='right' — x задаёт правый край (меню под кнопкой «…» у правого края сообщения).
 */
export function ContextMenu({ x, y, label, align = 'left', anchorRef, onClose, children }: { x: number; y: number; label: string; align?: 'left' | 'right'; anchorRef?: RefObject<HTMLElement | null>; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const host = useFullscreenHost()

  // размер знаем только после отрисовки: меню у края окна сдвигаем, чтобы влезло целиком.
  // Ставим прямо в стиль до показа кадра — без второй отрисовки
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const left = align === 'right' ? x - el.offsetWidth : x
    el.style.left = `${Math.max(8, Math.min(left, window.innerWidth - el.offsetWidth - 8))}px`
    const viewport = isMobileNow() && window.visualViewport?.scale === 1 ? window.visualViewport : null
    const top = viewport?.offsetTop ?? 0
    const height = viewport?.height ?? window.innerHeight
    el.style.top = `${Math.max(top + 8, Math.min(y, top + height - el.offsetHeight - 8))}px`
    // «Ссылка скопирована» короче исходной подписи — меню не должно сжиматься под курсором
    el.style.minWidth = `${el.offsetWidth}px`
  }, [x, y, align])

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchorRef?.current?.contains(e.target as Node)) onClose()
    }
    // Esc закрывает только меню: слой настроек или окно под ним остаются открытыми
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('pointerdown', onDown)
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('resize', onClose)
    window.visualViewport?.addEventListener('resize', onClose)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('resize', onClose)
      window.visualViewport?.removeEventListener('resize', onClose)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [onClose, anchorRef])

  const menu = (
    <div
      ref={ref}
      className={cx(styles.menu, 'klichat-popover')}
      role="menu"
      aria-label={label}
      style={{ '--popover-origin': align === 'right' ? 'top right' : 'top left' } as CSSProperties}
      onContextMenu={(e) => e.preventDefault()}
    >
      {children}
    </div>
  )
  // в полноэкранном режиме видно только развёрнутый элемент — рисуем меню внутрь него
  return createPortal(menu, host ?? document.body)
}

export function MenuTitle({ children }: { children: ReactNode }) {
  return <div className={styles.title}>{children}</div>
}

export function MenuSeparator() {
  return <div className={styles.separator} role="separator" />
}

/** Подпись группы пунктов: «Уведомления». */
export function MenuGroup({ children }: { children: ReactNode }) {
  return <div className={styles.group}>{children}</div>
}

interface ItemProps {
  ref?: Ref<HTMLButtonElement>
  icon?: ReactNode
  label: string
  /** Приглушённый текст справа: пояснение к пункту. */
  hint?: string | undefined
  onClick: () => void
  disabled?: boolean
  danger?: boolean
}

export function MenuItem({ ref, icon, label, hint, onClick, disabled = false, danger = false }: ItemProps) {
  return (
    <button ref={ref} type="button" role="menuitem" className={cx(styles.item, danger && styles.danger)} disabled={disabled} onClick={onClick}>
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <span className={styles.label}>{label}</span>
      {hint ? <span className={styles.hint}>{hint}</span> : null}
    </button>
  )
}

/** Пункт-переключатель из группы: галочка у выбранного. */
export function MenuRadio({ label, hint, checked, onClick }: { label: string; hint?: string | undefined; checked: boolean; onClick: () => void }) {
  return (
    <button type="button" role="menuitemradio" aria-checked={checked} className={cx(styles.item, styles.radio)} onClick={onClick}>
      <span className={styles.label}>{label}</span>
      {hint ? <span className={styles.hint}>{hint}</span> : null}
      <span className={cx(styles.mark, checked && styles.markOn)} aria-hidden="true">{checked ? <Check size={12} strokeWidth={3} /> : null}</span>
    </button>
  )
}

/**
 * Пункт «скопировать»: после копирования подпись меняется на done (например, «Ссылка скопирована»),
 * и через мгновение меню закрывается само — как кнопки «Скопировать» в настройках.
 */
export function MenuCopy({ icon, label, done, text, onDone }: { icon: ReactNode; label: string; done: string; text: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])
  const copy = () => {
    void navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        timer.current = setTimeout(onDone, 700)
      })
      .catch(() => undefined)
  }
  return (
    <button type="button" role="menuitem" className={cx(styles.item, copied && styles.done)} onClick={copy} aria-live="polite">
      <span className={styles.icon}>{copied ? <Check size={16} /> : icon}</span>
      <span className={styles.label}>{copied ? done : label}</span>
    </button>
  )
}
